import conductionLogo from '@/assets/images/conduction-logo.png';
import { AuthContext } from '@/context/AuthContext';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { Classroom } from '@/javascript/user-objects';
import { Utils } from '@/javascript/utils';
import { css, keyframes } from '@emotion/react';
import {
	ActionIcon,
	Box,
	Burger,
	Button,
	Flex,
	Group,
	Image,
	Menu,
	Text,
	TextInput,
	Tooltip,
	useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
	IconAlertCircle,
	IconCalendarEvent,
	IconCheck,
	IconChevronDown,
	IconDeviceFloppy,
	IconFileExport,
	IconFolder,
	IconKeyOff,
	IconLogout,
	IconMusic,
	IconSend,
	IconSettings,
	IconShare
} from '@tabler/icons-react';
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import appSettings from '../../appsettings.json';
import ClassroomProjectsBrowser from '../modals/classrooms/ClassroomProjectsBrowser';
import ClassroomsBrowser from '../modals/classrooms/ClassroomsBrowser';
import ClassroomUsersBrowser from '../modals/classrooms/ClassroomUsersBrowser';
import LiveEventInstrumentalistInterface from '../modals/live-events/LiveEventInstrumentalistInterface';
import LiveEventManager from '../modals/live-events/LiveEventManager';
import LiveEventsBrowser from '../modals/live-events/LiveEventsBrowser';
import LiveEventSignUp from '../modals/live-events/LiveEventSignUp';
import SendToLiveEventMusiciansForm from '../modals/live-events/SendToMusiciansLiveForm';
import SongsBrowser from '../modals/songs/SongsBrowser';
import ExportMenu from '../modals/user/ExportMenu';
import PublishProjectForm from '../modals/user/PublishProjectForm';
import SendToMusiciansForm from '../modals/user/SendToMusiciansForm';
import UserLoginForm from '../modals/user/UserLoginForm';
import ProjectSelect from '../modals/user/UserProjectsBrowser';

const shimmerRotate = keyframes`
to {
    transform: translate(0px, 1px) rotate(1.2deg);
    background-position-x: 0%;
}
`;

const bounce = keyframes`
to {
    transform: translate(0px, 2px);
    box-shadow: 2px 2px 2px 0 rgba(0, 0, 0, 0.3);
    background-position-x: 0%;
}
`;

const sendToMusicianButtonAnimation = css`
  transition: all 1s ease-in-out;
  background: linear-gradient(-30deg, #440814 40%, #8a2d40 50%, #440814 60%);
  background-size: 500%;
  box-shadow: 3px 3px 3px 0 rgba(0, 0, 0, 0.3);
  background-position: 100%;
  transform: translate(0px, -1px) rotate(-1.2deg);
  animation: ${shimmerRotate} 4s alternate-reverse infinite ease-in-out;

  &:hover {
    transform: translate(0px, -2px);
    box-shadow: 6px 6px 6px 0 rgba(0, 0, 0, 0.3);
    animation: ${bounce} 1s alternate-reverse infinite ease-in-out;
  }
`;

type NavAction = {
	key: string;
	label: string;
	icon: React.ReactNode;
	active: boolean;
	onClick: () => void;
	disabled?: boolean;
	hideInline?: boolean;
};


const Header: React.FC = () => {
	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error('The top bar must be a child of AuthContext.');
	}

	const { saveProject, project, updateProjectName, user } = auth;
	const location = useLocation();
	const path = location.pathname;

	const theme = useMantineTheme();

	const form = useForm({
		initialValues: { projectName: project?.getName() || '' },
		validate: {
			projectName: (value) =>
				value.trim().length === 0 ? 'Give your project a name!' : null,
		},
	});

	useEffect(() => {
		if (!project) {
			// No project: clear the field
			form.setFieldValue("projectName", "");
			return;
		}

		// New project loaded: hydrate the form with its name
		const name = project.getName() ?? "";
		form.setFieldValue("projectName", name);
	}, [project?.getId()]);

	useEffect(() => {
		if (!project) return;

		const formName = (form.values.projectName ?? "").trim();
		const currentName = (project.getName() ?? "").trim();

		// Already in sync? Do nothing.
		if (formName === currentName) return;

		// Otherwise, push the form value into the project
		updateProjectName(formName);
	}, [form.values.projectName, project, updateProjectName]);

	const handleSaveClick = () => {
		if (form.validate().hasErrors) {
			return;
		}

		if (user === null || user.roles.includes('Guest')) {
			Utils.displayNotification(
				'You must be logged in to save a project.',
				undefined,
				'blue',
				<IconAlertCircle size={16} />
			);

			return;
		}

		saveProject();
	};

	const handleSendToMusicianClick = useCallback(() => {
		// Skip validation if beginner tour is active
		if (!auth.beginnerTourActive && form.validate().hasErrors) {
			return;
		}

		if (auth.token === null) {
			Utils.displayNotification(
				'You must be logged in to send a project to musicians.',
				undefined,
				'blue',
				<IconAlertCircle size={16} />
			);

			return;
		}

		if (project !== null) {
			if (auth.beginnerTourActive && auth.currentTourStep === 6) {
				auth.moveToNextTourStep();
			}

			if (
				auth.activeParticipatingLiveEvent !== null ||
				auth.activeAdministratingLiveEvent !== null
			) {
				setShowSendToMusiciansLiveModal(true);
			} else {
				setShowSendToMusiciansModal(true);
			}
		}
	}, [project, auth, form]);

	const projectCreator = project?.getUser();

	const canEditProject =
		project !== null &&
		!project.isLocked() &&
		user !== null &&
		project.userCanEdit(user);

	const hasProjectName =
		project !== null && project.getName().trim().length > 0;

	const hasProjectContent = project !== null && project.hasItems();

	const deviceLayout = useDeviceLayout();

	/* ------------------------------ modals ------------------------------ */

	const [showLoginModal, setShowLoginModal] = auth.loginModalState;
	const [showUserProjectsBrowserModalFor, setShowUserProjectsBrowserModalFor] = useState<string | undefined>(undefined);
	const [showExportProjectModal, setShowExportProjectModal] = useState(false);
	const [showPublishProjectModal, setShowPublishProjectModal] = useState(false);
	const [showClassroomProjectsBrowserModalFor, setShowClassroomProjectsBrowserModalFor] = useState<Classroom | undefined>(undefined);
	const [showClassroomUsersBrowserModalFor, setShowClassroomUsersBrowserModalFor] = useState<Classroom | undefined>(undefined);
	const [showClassroomsBrowserModal, setShowClassroomsBrowserModal] = useState(false);
	const [showSongsBrowserModal, setShowSongsBrowserModal] = useState(false);
	const [showLiveEventsBrowserModal, setShowLiveEventsBrowserModal] = useState(false);
	const [showManageLiveEventModalFor, setShowManageLiveEventModalFor] = useState<string | undefined>(undefined);
	const [showLiveEventInstrumentalistInterfaceFor, setShowLiveEventInstrumentalistInterfaceFor] = useState<string | undefined>(undefined);
	const [showLiveEventSignUpModal, setShowLiveEventSignUpModal] = useState(false);
	const [showSendToMusiciansModal, setShowSendToMusiciansModal] = useState(false);
	const [showSendToMusiciansLiveModal, setShowSendToMusiciansLiveModal] = useState(false);
	const renderModals = () => (
		<>
			{showLoginModal && (
				<UserLoginForm
					onClose={() => setShowLoginModal(false)}
				/>
			)}

			{showUserProjectsBrowserModalFor !== undefined && (
				<ProjectSelect
					userId={showUserProjectsBrowserModalFor}
					onClose={() => setShowUserProjectsBrowserModalFor(undefined)}
				/>
			)}

			{showExportProjectModal && project && (
				<ExportMenu
					onClose={() => setShowExportProjectModal(false)}
				/>
			)}

			{showPublishProjectModal && (
				<PublishProjectForm
					onClose={() => setShowPublishProjectModal(false)}
				/>
			)}

			{showClassroomProjectsBrowserModalFor !== undefined && (
				<ClassroomProjectsBrowser
					classroom={showClassroomProjectsBrowserModalFor}
					onClose={() => setShowClassroomProjectsBrowserModalFor(undefined)}
					onProjectSelected={() => { }}
				/>
			)}

			{showClassroomsBrowserModal && (
				<ClassroomsBrowser
					onClose={() => setShowClassroomsBrowserModal(false)}
				/>
			)}

			{showClassroomUsersBrowserModalFor !== undefined && (
				<ClassroomUsersBrowser
					classroom={showClassroomUsersBrowserModalFor}
					onClose={() => setShowClassroomUsersBrowserModalFor(undefined)}
				/>
			)}

			{showSongsBrowserModal && (
				<SongsBrowser
					onClose={() => setShowSongsBrowserModal(false)}
				/>
			)}

			{showLiveEventsBrowserModal && (
				<LiveEventsBrowser
					onClose={() => setShowLiveEventsBrowserModal(false)}
				/>
			)}

			{showManageLiveEventModalFor !== undefined && (
				<LiveEventManager
					liveEventId={showManageLiveEventModalFor}
					onClose={() => setShowManageLiveEventModalFor(undefined)}
				/>
			)}

			{showLiveEventInstrumentalistInterfaceFor !== undefined && (
				<LiveEventInstrumentalistInterface
					liveEventId={showLiveEventInstrumentalistInterfaceFor}
					onClose={() => setShowLiveEventInstrumentalistInterfaceFor(undefined)}
				/>
			)}

			{showLiveEventSignUpModal && (
				<LiveEventSignUp
					onClose={() => setShowLiveEventSignUpModal(false)}
				/>
			)}

			{showSendToMusiciansModal && (
				<SendToMusiciansForm
					onClose={() => setShowSendToMusiciansModal(false)}
				/>
			)}

			{showSendToMusiciansLiveModal && (
				<SendToLiveEventMusiciansForm
					onClose={() => setShowSendToMusiciansLiveModal(false)}
				/>
			)}
		</>
	);

	/* ------------------------------ project toolbar ----------------------------- */

	const renderProjectTitleDisplay = () => (
		<>
			<Box maw={deviceLayout.isTabletOrSmaller ? '30vw' : '15vw'}>
				{project && (
					<Text truncate="end" size="md" fw={700} c="red">
						{project.getName() || (
							<IconKeyOff size={16} />
						)}
					</Text>
				)}
			</Box>
			<Text
				size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}
				hidden={
					projectCreator === undefined ||
					projectCreator.id === user?.id
				}
			>
				by {projectCreator?.username}
			</Text>
		</>
	);

	const renderProjectEditSection = () => {
		if (!project) {
			return null;
		}

		if (!canEditProject) {
			return renderProjectTitleDisplay();
		}

		return (
			<Group gap="xs" wrap="nowrap" align="center">
				<TextInput
					{...form.getInputProps('projectName')}
					placeholder="Project Name"
					disabled={
						project === null ||
						project.isLocked() ||
						!project.userCanEdit(user!)
					}
					size="xs"
					w={deviceLayout.isTabletOrSmaller ? deviceLayout.isPhoneOrSmaller ? 125 : 140 : 160}
					rightSection={
						<Tooltip
							label={
								project.isDirty() ? 'Save' : 'This project was saved'
							}
						>
							<ActionIcon
								onClick={handleSaveClick}
								disabled={
									!project?.isDirty() ||
									auth.saveStatus === 'processing'
								}
								loading={auth.saveStatus === 'processing'}
								variant="subtle"
								size={deviceLayout.isPhoneOrSmaller ? 18 : "xs"}
							>
								{project.isDirty() ? <IconDeviceFloppy /> : <IconCheck />}
							</ActionIcon>
						</Tooltip>
					}
				/>

				{!user!.roles.includes('Guest') && !collapseToBurger && (
					<Menu
						transitionProps={{ transition: 'pop-top-left' }}
						trigger="click-hover"
						openDelay={100}
						position="bottom-start"
					>
						<Menu.Target>
							<Tooltip label="Share">
								<Button
									variant="subtle"
									size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}
									leftSection={<IconShare size={16} />}
									rightSection={<IconChevronDown size={16} />}
								>
									{!deviceLayout.isTabletOrSmaller && (
										<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}>Share</Text>
									)}
								</Button>
							</Tooltip>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Item
								disabled={
									project === null ||
									project.isLocked() ||
									!project.userCanEdit(user!) ||
									project.isDirty()
								}
								key={'share-conduction-social'}
								onClick={() => setShowPublishProjectModal(true)}
							>
								<Group w={220}>
									<Image
										src={conductionLogo}
										alt="Conduction Social"
										height={24}
										radius="sm"
									/>
									<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}>Post to Conduction Social</Text>
								</Group>
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				)}
			</Group>
		);
	};

	const renderSendToMusiciansButton = () => {
		if (!project) return null;

		return (
			<Tooltip
				withArrow
				label="Name your project first!"
				disabled={hasProjectName || auth.beginnerTourActive}
			>
				<Button
					onClick={handleSendToMusicianClick}
					disabled={!hasProjectContent}
					variant="filled"
					sx={
						project && project.hasItems()
							? sendToMusicianButtonAnimation
							: {}
					}
					className="beginner-tour-send-to-musicians-button"
					size="xs"
					leftSection={!deviceLayout.isPhoneOrSmaller && <IconSend size={16} />}
					p={deviceLayout.isPhoneOrSmaller ? 3 : undefined}
				>
					<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}>Send to Musicians</Text>
				</Button>
			</Tooltip>
		);
	};

	/* ----------------------------- nav measurement ------------------------------ */

	const navContainerRef = useRef<HTMLDivElement | null>(null);
	const measureRef = useRef<HTMLDivElement | null>(null);
	const [collapseToBurger, setCollapseToBurger] = useState(false);
	const [discoveredCollapseBreakpoint, setDiscoveredCollapseBreakpoint] = useState<number | null>(null);

	const recomputeOverflow = useCallback(() => {
		const visible = navContainerRef.current;
		const measure = measureRef.current;
		if (!visible || !measure) return;

		const needed = measure.scrollWidth;
		const available = visible.clientWidth;

		if (discoveredCollapseBreakpoint !== null && available <= discoveredCollapseBreakpoint + 20) { // add some hysteresis
			setCollapseToBurger(true);
			return;
		}

		const collapse = needed > available;
		if (collapse && (discoveredCollapseBreakpoint === null || available > discoveredCollapseBreakpoint)) {
			setDiscoveredCollapseBreakpoint(needed);
		}
		setCollapseToBurger(collapse);
	}, [discoveredCollapseBreakpoint]);

	useEffect(() => {
		const visible = navContainerRef.current;
		const measure = measureRef.current;
		if (!visible || !measure) return;

		const ro = new ResizeObserver(() => {
			recomputeOverflow();
		});

		ro.observe(visible);
		ro.observe(measure);

		window.addEventListener('resize', recomputeOverflow);
		recomputeOverflow();

		return () => {
			ro.disconnect();
			window.removeEventListener('resize', recomputeOverflow);
		};
	}, [recomputeOverflow, user, project, deviceLayout.isTabletOrSmaller]);

	/* ------------------------------ nav definitions ----------------------------- */

	const navActions: NavAction[] = useMemo(() => {
		if (!user) return [];

		const items: NavAction[] = [];

		// My Projects (non-admin User/Guest)
		if (
			!user.roles.includes('ConductionAdmin') &&
			(user.roles.includes('User') || user.roles.includes('Guest'))
		) {
			items.push({
				key: 'my-projects',
				label: 'My Projects',
				icon: <IconFolder size={16} />,
				active: showUserProjectsBrowserModalFor !== undefined,
				onClick: () => {
					if (user.roles.includes('Guest')) {
						Utils.displayNotification(
							'You must be logged in to save and view projects.',
							undefined,
							'blue',
							<IconAlertCircle size={16} />
						);
						return;
					}
					setShowUserProjectsBrowserModalFor(user.id);
				},
			});
		}

		// Class Projects / Class Members (ClassroomAdmin)
		if (user.roles.includes('ClassroomAdmin')) {
			const classroom = (auth.classroom as Classroom | null);
			items.push({
				key: 'class-projects',
				label: 'Class Projects',
				icon: <IconFolder size={16} />,
				active: showClassroomProjectsBrowserModalFor !== undefined,
				onClick: () => {
					if (!classroom) return;
					setShowClassroomProjectsBrowserModalFor(classroom);
				},
				disabled: !classroom,
			});
			items.push({
				key: 'class-members',
				label: 'Class Members',
				icon: <IconSettings size={16} />,
				active: showClassroomUsersBrowserModalFor !== undefined,
				onClick: () => {
					if (!classroom) return;
					setShowClassroomUsersBrowserModalFor(classroom);
				},
				disabled: !classroom,
			});
		}

		// Export (non-Student, non-Guest)
		if (
			!user.roles.includes('Student') &&
			!user.roles.includes('Guest')
		) {
			items.push({
				key: 'export',
				label: 'Export',
				icon: <IconFileExport size={16} />,
				active: showExportProjectModal,
				onClick: () => setShowExportProjectModal(true),
				disabled:
					!project ||
					!project.hasItems() ||
					!hasProjectName,
			});
		}

		// Manage Songs (ConductionAdmin or Transcriber)
		if (
			user.roles.includes('ConductionAdmin') ||
			user.roles.includes('Transcriber')
		) {
			items.push({
				key: 'songs',
				label: 'Songs',
				icon: <IconMusic size={16} />,
				active: showSongsBrowserModal,
				onClick: () => setShowSongsBrowserModal(true),
			});
		}

		// Manage Classrooms (ConductionAdmin)
		if (user.roles.includes('ConductionAdmin')) {
			items.push({
				key: 'classrooms',
				label: 'Classrooms',
				icon: <IconSettings size={16} />,
				active: showClassroomsBrowserModal,
				onClick: () => setShowClassroomsBrowserModal(true),
			});
		}

		// Manage Live Events (ConductionAdmin)
		if (user.roles.includes('ConductionAdmin')) {
			items.push({
				key: 'live-events',
				label: 'Live Events',
				icon: <IconCalendarEvent size={16} />,
				active: showLiveEventsBrowserModal,
				onClick: () => setShowLiveEventsBrowserModal(true),
			});
		}

		// Manage Event (live event admin)
		if (auth.activeAdministratingLiveEvent != null) {
			const id = auth.activeAdministratingLiveEvent.id;
			items.push({
				key: 'manage-event',
				label: 'Manage Event',
				icon: <IconSettings size={16} />,
				active: showManageLiveEventModalFor === id,
				onClick: () => setShowManageLiveEventModalFor(id),
			});
		}

		// Event Performance Queue (Instrumentalist)
		if (
			user.roles.includes('Instrumentalist') &&
			auth.activePlayingLiveEvent != null
		) {
			const id = auth.activePlayingLiveEvent.id;
			items.push({
				key: 'event-queue',
				label: 'Event Performance Queue',
				icon: <IconMusic size={16} />,
				active: showLiveEventInstrumentalistInterfaceFor !== undefined,
				onClick: () => setShowLiveEventInstrumentalistInterfaceFor(id),
			});
		}

		// Share → Conduction Social (collapsible nav item)
		if (!user.roles.includes('Guest')) {
			const canShare =
				!!project &&
				!project.isLocked() &&
				project.userCanEdit(user) &&
				!project.isDirty();

			items.push({
				key: 'share-conduction-social',
				label: 'Share Project to Social',
				icon: <IconShare size={16} />,
				active: showPublishProjectModal,
				onClick: () => {
					if (!canShare) return;
					setShowPublishProjectModal(true);
				},
				disabled: !canShare,
				hideInline: true
			});
		}

		// Conduction Social (collapsible nav item)
		items.push({
			key: 'conduction-social',
			label: 'Visit Conduction Social',
			icon: <Image
				src={conductionLogo}
				alt="Conduction Social"
				w={24}
				radius="sm"
			/>,
			active: false,
			onClick: () =>
				window.open(
					`${appSettings.ConductionSocialUrl}activity-feed`,
					'_blank'
				),
			hideInline: true
		});

		return items;
	}, [
		user,
		auth.classroom,
		auth.activeAdministratingLiveEvent,
		auth.activePlayingLiveEvent,
		location.search,
		path,
		project,
		hasProjectName,
	]);

	/* ------------------------------ nav renderers ------------------------------- */

	const renderInlineNav = () => {
		if (!user) return null;

		return (
			<Group gap={deviceLayout.isTabletOrSmaller ? 4 : 'sm'} wrap="nowrap" align="center">
				{navActions
					.filter((item) => collapseToBurger || !item.hideInline)
					.map((item) => (
						<Button
							key={item.key}
							w="auto"
							size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}
							disabled={item.disabled}
							onClick={item.onClick}
							bg={
								item.active
									? theme.colors.white?.[6]
									: undefined
							}
							c={
								item.active
									? theme.colors.blue?.[8]
									: undefined
							}
							leftSection={item.icon}
						>
							<Text size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}>{item.label}</Text>
						</Button>
					))}
			</Group>
		);
	};

	const renderBurgerNav = () => {
		if (!user) return null;

		return (
			<Menu
				transitionProps={{ transition: 'pop-top-left' }}
				position="bottom-start"
				openDelay={100}
			>
				<Menu.Target>
					<Burger aria-label="Open navigation menu" size="sm" w={deviceLayout.isPhoneOrSmaller ? 12 : "auto"} />
				</Menu.Target>
				<Menu.Dropdown>
					{navActions.map((item) => (
						<Menu.Item
							key={item.key}
							onClick={item.onClick}
							disabled={item.disabled}
							fw={item.active ? 700 : 400}
							leftSection={item.icon}
						>
							{item.label}
						</Menu.Item>
					))}
				</Menu.Dropdown>
			</Menu>
		);
	};

	/* ------------------------------- user menu ---------------------------------- */

	const renderUserMenu = () => {
		if (!user) return null;

		if (user.roles.includes('Guest')) {
			return (
				<Button
					leftSection={<IconLogout size={16} />}
					onClick={() => {
						auth.logout();
					}}
					variant="outline"
					size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}
				>
					<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}>Login{!deviceLayout.isPhoneOrSmaller && " or Create Account"}</Text>
				</Button>
			);
		}

		return (
			<Menu
				transitionProps={{ transition: 'pop-top-right' }}
				openDelay={100}
				position="bottom-end"
			>
				<Menu.Target>
					<Button variant="subtle" p={0} justify="right">
						<Group gap="xs" wrap="nowrap" align="center">
							{auth.profileAvatar}
							{!deviceLayout.isPhoneOrSmaller && (
								<Text
									fz={14}
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										maxWidth: 350,
									}}
								>
									{user.username}
								</Text>
							)}
							{!deviceLayout.isPhoneOrSmaller && <IconChevronDown size={16} />}
						</Group>
					</Button>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item
						leftSection={<IconLogout size={16} />}
						onClick={() => {
							auth.logout();
						}}
					>
						<Text size={deviceLayout.isTabletOrSmaller ? '12px' : '14px'}>Logout</Text>
					</Menu.Item>
					<Menu.Item
						leftSection={<IconSettings size={16} />}
						onClick={() => setShowLiveEventSignUpModal(true)}
					>
						<Text size={deviceLayout.isTabletOrSmaller ? '12px' : '14px'}>
							Live Events
						</Text>
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
		);
	};

	/* --------------------------------- render ----------------------------------- */

	// Not logged in: keep simple CTA on left, no nav/burger.
	if (!user) {
		return (
			<Box px="md" style={{ width: '100%' }}>
				{renderModals()}

				<Flex
					align="center"
					gap="sm"
					w="100%"
					h={40}
					justify="space-between"
				>
					<Group align="center" gap="xs">
						<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : ""}>
							Log in to save projects and build your library!
						</Text>
						<Button
							onClick={() => {
								setShowLoginModal(true);
							}}
							variant="filled"
							size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}
						>
							<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}>Log in</Text>
						</Button>
					</Group>

					<Group align="center" gap="xs">
						<Button
							variant="subtle"
							size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}
							leftSection={
								<Image
									src={conductionLogo}
									alt="Conduction Social"
									height={24}
									radius="sm"
								/>
							}
							onClick={() =>
								window.open(
									`${appSettings.ConductionSocialUrl}activity-feed`,
									'_blank'
								)
							}
							w={10}
						>
							<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"} p={0}>
								{deviceLayout.isTabletOrSmaller
									? 'Social'
									: 'See songs by friends!'}
							</Text>
						</Button>
					</Group>
				</Flex>
			</Box>
		);
	}

	// Logged-in layout: project toolbar (left) | nav (center) | user menu + social (right)
	return (
		<Box
			px={deviceLayout.isPhoneOrSmaller ? 2 : 'md'}
			style={{ width: '100%' }}
			ref={navContainerRef}
		>
			{renderModals()}

			<Group ref={measureRef} w="fit-content" miw="100%">
				<Flex
					align="center"
					gap="sm"
					w="100%"
					h={40}
					justify="space-between"
					wrap="nowrap"
				>
					{/* Left: nav area that collapses to burger when overflowing and project toolbar (always visible, never in burger) */}
					<Group wrap="nowrap">
						{navActions.length > 0 &&
							(collapseToBurger ? renderBurgerNav() : renderInlineNav())}

						<Group gap="xs" wrap="nowrap" align="center">
							{renderProjectEditSection() || renderProjectTitleDisplay()}
						</Group>
					</Group>

					{/* Center: Send to Musicians button */}
					<Group h="100%" mx="auto" align="center">
						{renderSendToMusiciansButton()}
					</Group>

					{/* Right: user menu + Social button */}
					<Group justify="flex-end" wrap="nowrap" gap="xs">
						{renderUserMenu()}
						{!collapseToBurger && (
							<Button
								variant="subtle"
								size={deviceLayout.isPhoneOrSmaller ? "compact-xs" : "xs"}
								leftSection={
									<Image
										src={conductionLogo}
										alt="Conduction Social"
										w={24}
										radius="sm"
									/>
								}
								onClick={() =>
									window.open(
										`${appSettings.ConductionSocialUrl}activity-feed`,
										'_blank'
									)
								}
							>
								<Text size={deviceLayout.isPhoneOrSmaller ? "xs" : "sm"}>
									See songs by friends!
								</Text>
							</Button>
						)}
					</Group>
				</Flex>
			</Group>
		</Box>
	);
};

export default Header;
